import React, { useState, useEffect } from 'react';
import {
    OptionDefinition,
    OptionValues,
    OptionField,
    TextField as TextFieldType,
    SelectField as SelectFieldType,
    RadioGroupField as RadioGroupFieldType,
    CheckboxGroupField as CheckboxGroupFieldType,
    ContentField as ContentFieldType,
    ImageGroupField as ImageGroupFieldType,
    ColorGroupField as ColorGroupFieldType,
    TextareaField as TextareaFieldType,
    FileUploadField as FileUploadFieldType,
    ProductGroupField as ProductGroupFieldType
} from '../types/optionTypes';
import { TextField } from './option-fields/TextField';
import { SelectField } from './option-fields/SelectField';
import { CheckboxGroupField } from './option-fields/CheckboxGroupField';
import { RadioGroupField } from './option-fields/RadioGroupField';
import { ContentField } from './option-fields/ContentField';
import { ImageGroupField } from './option-fields/ImageGroupField';
import { ColorGroupField } from './option-fields/ColorGroupField';
import { TextareaField } from './option-fields/TextareaField';
import { FileUploadField } from './option-fields/FileUploadField';
import { ProductGroupField } from './option-fields/ProductGroupField';
import { evaluateCondition } from '../utils/logicEvaluator';
import { validateOptions } from '../utils/validator';

interface ProductOptionsRendererProps {
    optionData: OptionDefinition[];
    values: OptionValues;
    onValuesChange: (values: OptionValues) => void;
    selectedProduct?: any; // Pass current product for logic evaluation
    additionalContext?: Record<string, any>;
}

// colプロパティをTailwindのcol-spanクラスにマッピング
const getColSpanClass = (col: number): string => {
    const colMap: Record<number, string> = {
        1: 'col-span-1',
        2: 'col-span-2',
        3: 'col-span-3',
        4: 'col-span-4',
        5: 'col-span-5',
        6: 'col-span-6',
    };
    return colMap[col] || 'col-span-6';
};

export const ProductOptionsRenderer: React.FC<ProductOptionsRendererProps> = ({
    optionData,
    values,
    onValuesChange,
    selectedProduct,
    additionalContext = {}
}) => {
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        // Validate on change
        const newErrors = validateOptions(optionData, values, selectedProduct);
        setErrors(newErrors);
    }, [values, optionData, selectedProduct]);

    const handleFieldChange = (fieldName: string, value: any) => {
        onValuesChange({
            ...values,
            [fieldName]: value
        });
    };

    const renderField = (field: OptionField) => {
        // Skip inactive fields
        if (!field.active) {
            return null;
        }

        // Logic evaluation for visibility
        if (field.enableCl && field.relations) {
            const context = {
                values,
                product: { ...selectedProduct, ...additionalContext }
            };
            const isConditionMet = evaluateCondition(field.relations as any, context);
            const shouldShow = field.cl_rule === 'show' ? isConditionMet : !isConditionMet;

            if (!shouldShow) {
                return null;
            }
        } else if (field.cl_rule === 'hide' && field.enableCl) {
            if (field.enableCl) {
                return null;
            }
        }

        const fieldValue = values[field.name];
        const key = field.elementId || field.name;
        const colSpanClass = getColSpanClass(field.col || 6);

        const fieldWithProps = {
            ...field,
            error: !!errors[field.name],
            errorMessage: errors[field.name] // Pass error message if component supports it
        };

        const handleChange = (val: any) => handleFieldChange(field.name, val);

        switch (field.type) {
            case 'text':
            case 'number':
                return (
                    <div key={key} className={colSpanClass}>
                        <TextField
                            field={fieldWithProps as unknown as TextFieldType}
                            value={fieldValue}
                            onChange={handleChange}
                        />
                    </div>
                );

            case 'select':
                return (
                    <div key={key} className={colSpanClass}>
                        <SelectField
                            field={fieldWithProps as unknown as SelectFieldType}
                            value={fieldValue}
                            onChange={handleChange}
                        />
                    </div>
                );

            case 'radio-group':
                return (
                    <div key={key} className={colSpanClass}>
                        <RadioGroupField
                            field={fieldWithProps as unknown as RadioGroupFieldType}
                            value={fieldValue}
                            onChange={handleChange}
                        />
                    </div>
                );

            case 'checkbox-group':
                return (
                    <div key={key} className={colSpanClass}>
                        <CheckboxGroupField
                            field={field as CheckboxGroupFieldType}
                            value={fieldValue}
                            onChange={(val) => handleFieldChange(field.name, val)}
                        />
                    </div>
                );

            case 'content':
                return (
                    <div key={key} className={colSpanClass}>
                        <ContentField field={field as ContentFieldType} />
                    </div>
                );

            case 'image-group':
                return (
                    <div key={key} className={colSpanClass}>
                        <ImageGroupField
                            field={field as ImageGroupFieldType}
                            value={fieldValue}
                            onChange={(val) => handleFieldChange(field.name, val)}
                        />
                    </div>
                );

            case 'color-group':
                return (
                    <div key={key} className={colSpanClass}>
                        <ColorGroupField
                            field={field as ColorGroupFieldType}
                            value={fieldValue}
                            onChange={(val) => handleFieldChange(field.name, val)}
                        />
                    </div>
                );

            case 'textarea':
                return (
                    <TextareaField
                        field={field as TextareaFieldType}
                        value={fieldValue}
                        onChange={(val) => handleFieldChange(field.name, val)}
                    />
                );

            case 'file':
                return (
                    <FileUploadField
                        field={field as FileUploadFieldType}
                        value={fieldValue}
                        onChange={(val) => handleFieldChange(field.name, val)}
                    />
                );

            case 'productGroup':
                return (
                    <div key={key} className={colSpanClass}>
                        <ProductGroupField
                            field={field as ProductGroupFieldType}
                            value={fieldValue}
                            onChange={(val) => handleFieldChange(field.name, val)}
                        />
                    </div>
                );

            case 'separator':
                return (
                    <div key={key} className={colSpanClass}>
                        <hr className="my-4 border-gray-300" />
                    </div>
                );

            default:
                // For Phase 2/3 fields, show a placeholder
                return (
                    <div key={key} className={colSpanClass}>
                        <p className="text-xs text-gray-400 italic">
                            {field.type} field (実装予定: Phase 2)
                        </p>
                    </div>
                );
        }
    };

    if (!optionData || optionData.length === 0) {
        return null;
    }

    const definition = optionData[0];

    return (
        <div className="space-y-8">
            {Object.entries(definition.fields).map(([sectionKey, section]) => {
                // Skip inactive sections
                if (section.extra.status !== 1) {
                    return null;
                }

                // Logic evaluation for section visibility
                if (section.extra.enableCl && section.extra.relations) {
                    const context = {
                        values,
                        product: { ...selectedProduct, ...additionalContext }
                    };
                    const isConditionMet = evaluateCondition(section.extra.relations as any, context);
                    const shouldShow = section.extra.cl_rule === 'show' ? isConditionMet : !isConditionMet;

                    if (!shouldShow) {
                        return null;
                    }
                } else if (section.extra.cl_rule === 'hide' && section.extra.enableCl) {
                    return null;
                }

                return (
                    <div key={sectionKey} className="border-t border-gray-200 pt-6 first:border-t-0 first:pt-0">
                        {section.extra.show_title && section.extra.name && (
                            <h4 className="text-base font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-100">
                                {section.extra.name}
                            </h4>
                        )}

                        <div className="grid grid-cols-6 gap-4 md:gap-6">
                            {section.fields.map((fieldRow, rowIndex) => (
                                <React.Fragment key={`row-${rowIndex}`}>
                                    {fieldRow.map((field) => renderField(field))}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
